import React, { useEffect, useState, useRef, useCallback, useMemo, Fragment } from 'react';
import styles from './InPlayerCommentsOverlay.module.scss';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '../../../../context/UserContext';
import Draggable from 'react-draggable';
import { CustomButton } from '../../../../components/Button/Button';
import Avatar from '../../../../components/Avatar/Avatar';
import CloseButton from '../../../../components/CloseButton/CloseButton';
import cx from 'classnames';
import { useInPlayerCommentContext } from '../../../../context/InPlayerCommentContext';
import { useVideoProgressContext } from '../../../../context/VideoProgressContext';
import { secondsToTimeString } from '../../../../utils/TimeUtil';
import ReactDOM from 'react-dom';
import { BACKSPACE_KEY_CODE, ENTER_KEY_CODE, ESCAPE_KEY_CODE } from '../../../../constants/common';
import Icon from '../../../../components/Icon/Icon';
import HoverPlayerContent from '../../../../components/PlayerComment/HoverPlayerContent';
import screenfull from 'screenfull';
import useVideoContentDimensions from '../../../../hooks/useVideoContentDimensions';

const DEFAULT_MARGIN = 10;
const MIN_HEIGHT = 50;
const SECONDS_IN_HOUR = 3600;

const InPlayerCommentsOverlay = ({ isFirstPlayed, playing, containerElementNode, maxDimensions, mixedOrientation }) => {

    const {
        addNotesMode,
        initPosition,
        timestampComments,
        highlightCommentId,
        setNewPositionComment,
        exitAddNotesMode,
        handleCommentClick,
    } = useInPlayerCommentContext();

    const { videoProgress } = useVideoProgressContext();

    const recalculatePositionTimeRef = useRef(null);

    const timestamp = useMemo(() => Math.trunc(videoProgress.milliseconds / 1000), [videoProgress.milliseconds]);

    const [newCommentPosition, setNewCommentPosition] = useState(null);
    const [isMoving, setIsMoving] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [inputFieldFocus, setInputFieldFocus] = useState(false);
    const [textareaHeight, setTextareaHeigh] = useState(0);
    const [saveNewComment, setSaveNewComment] = useState(false);

    const { t } = useTranslation();

    const { user } = useUserContext();

    const { offsetHeight,
        offsetWidth,
        videoContentWidth,
        videoContentHeight, } = useVideoContentDimensions({ parentNode: containerElementNode, maxDimensions, mixedOrientation, });


    const [hoverCommentId, setHoverCommentId] = useState(null);
    const [prevContainerDimensions, setPrevContainerDimensions] = useState(null);

    const { top = null, left = null, width = null, height = null } = containerElementNode?.getBoundingClientRect() ?? {};

    const videoContentPosition = useMemo(() => {
        return { x: (offsetWidth - videoContentWidth) / 2, y: (offsetHeight - videoContentHeight) / 2 };
    }, [left, top, offsetWidth, offsetHeight, videoContentWidth, videoContentHeight])

    const second = useMemo(() => Math.trunc(videoProgress.milliseconds / 1000), [videoProgress.milliseconds]);

    const points = useMemo(() => {
        if (!playing && isFirstPlayed && timestampComments) {
            if (timestampComments[second]) {
                return Object.values(timestampComments[second].filter(comment => comment?.position?.x));
            }
        }
        return [];
    }, [timestampComments, playing, second, isFirstPlayed]);

    const handleDrag = useCallback((e, dragData) => {
        setNewCommentPosition({ x: dragData.x, y: dragData.y });
        setIsMoving(true);
    }, [videoContentWidth, videoContentPosition]);

    const handleDragStop = (e) => {
        setIsMoving(false);
    }

    const handleCloseComment = () => {
        setNewCommentPosition(null);
        setCommentText('');
    }

    const handleCommentPointHover = (id) => () => {
        setHoverCommentId(id);
    }

    const handleCommentTextChange = useCallback((e) => {
        if (e.target?.scrollHeight > MIN_HEIGHT) {
            setTextareaHeigh(e.target.scrollHeight);
        }
        setCommentText(e.target.value);
    }, []);

    const handleSave = useCallback(() => {
        const position = `[${Math.round((newCommentPosition?.x / width) * 100)}, ${Math.round((newCommentPosition?.y / height) * 100)}]`;
        setNewPositionComment({ content: `${secondsToTimeString(timestamp)} ${position} ${commentText}`, username: user?.username, authorName: user?.userFullName });
        setCommentText('');
        setNewCommentPosition(null);
    }, [left, top, commentText, newCommentPosition, timestamp, user, width, height]);

    // The child components you want to add to the video element
    const draggableChild = (
        <Draggable bounds={{ left: videoContentPosition?.x, top: videoContentPosition?.y, right: videoContentPosition?.x + videoContentWidth, bottom: videoContentPosition?.y + videoContentHeight }} positionOffset={{ x: 0, y: 0 }} position={newCommentPosition} onDrag={handleDrag} onStop={handleDragStop}>
            <div className={styles.dotContainer}>
                <div className={styles.noteDot} />
            </div>
        </Draggable>
    );

    const commentWrapperChild = (
        <div
            className={styles.commentWrapper}
            style={{
                top: `${top}px`,
                left: `${left}px`,
                transform: `translate(${newCommentPosition?.x + DEFAULT_MARGIN}px, ${newCommentPosition?.y + DEFAULT_MARGIN}px)${(newCommentPosition?.x / width > 0.5) ? ` translateX(calc(-100% - ${DEFAULT_MARGIN * 3}px))` : ''}${(newCommentPosition?.y / height > 0.5) ? ` translateY(calc(-100% - ${DEFAULT_MARGIN * 3}px))` : ''}`,
            }}
        >
            <div className={styles.commentContainer}>
                <Avatar
                    image={user?.avatarUri}
                    title={user?.userFullName}
                    size="medium"
                    oneLetterAcronym={true}
                />
                <div
                    className={cx(styles.textareaWrapper, { [styles.focus]: inputFieldFocus })}
                    onFocus={() => setInputFieldFocus(true)}
                    onBlur={() => setInputFieldFocus(false)}
                >
                    <div className={styles.scrollableTextarea}>
                        <textarea
                            ref={inputRef}
                            className={styles.textarea}
                            value={commentText}
                            onChange={handleCommentTextChange}
                            onKeyDown={handleKeyEvent}
                            onPaste={handleKeyEvent}
                            style={{ textIndent: timestamp > SECONDS_IN_HOUR ? '4em' : '3em', height: `${textareaHeight}px` }}
                            aria-label='Add a note'
                        />
                        <span className={styles.timestamp}>
                            {secondsToTimeString(timestamp)}
                        </span>
                    </div>
                    <CustomButton className={styles.addButton} variant='link' label={t('general.add')} onClick={handleSave} disabled={commentText.trim().length === 0} />
                </div>
                <CloseButton variant='primary' className={styles.closeBtn} onClose={handleCloseComment} />
            </div>
        </div>
    );

    const inputRef = useRef(null);

    const handleKeyEvent = e => {
        // Reset field height
        e.target.style.height = 'inherit';
        // Get the computed styles for the element
        e.target.style.height = `${Math.max(MIN_HEIGHT, e.target.scrollHeight)}px`;
    };

    const handleCancel = () => {
        exitAddNotesMode();
        setHoverCommentId(null);
        setNewCommentPosition(null);
    }

    useEffect(() => {
        if (initPosition !== null) {
            setNewCommentPosition({ x: initPosition.x - left, y: initPosition.y - top });
            setPrevContainerDimensions({ width, height, top, left, offsetHeight, offsetWidth, videoContentWidth, videoContentHeight });
        }
    }, [initPosition])

    useEffect(() => {
        if (addNotesMode && newCommentPosition !== null) {
            if (recalculatePositionTimeRef?.current !== null) {
                clearTimeout(recalculatePositionTimeRef.current);
                recalculatePositionTimeRef.current = null;
            }
            recalculatePositionTimeRef.current = setTimeout(() => {
                setNewCommentPosition(prevState => {
                    const oldLeftStartPos = (prevContainerDimensions.offsetWidth - prevContainerDimensions.videoContentWidth) / 2;
                    const newLeftStartPos = (offsetWidth - videoContentWidth) / 2;
                    const oldTopStartPos = (prevContainerDimensions.offsetHeight - prevContainerDimensions.videoContentHeight) / 2;
                    const newTopStartPos = (offsetHeight - videoContentHeight) / 2;

                    const prevPositionXInContent = (prevState.x - oldLeftStartPos) / prevContainerDimensions.videoContentWidth;
                    const prevPositionYInContent = (prevState.y - oldTopStartPos) / prevContainerDimensions.videoContentHeight;

                    const positionXInContent = (prevPositionXInContent * videoContentWidth) + newLeftStartPos;
                    const positionYInContent = (prevPositionYInContent * videoContentHeight) + newTopStartPos;
                    setNewCommentPosition({ x: positionXInContent, y: positionYInContent });
                    setPrevContainerDimensions({ width, height, top, left, offsetWidth, offsetHeight, videoContentWidth, videoContentHeight });
                })
            }, 100);
        }
    }, [offsetWidth, videoContentWidth, videoContentHeight, offsetHeight])

    useEffect(() => {
        const handleKeyListener = (event) => {
            switch (event.keyCode) {
                case ESCAPE_KEY_CODE:
                    handleCancel();
                    break;
                case BACKSPACE_KEY_CODE:
                    handleKeyEvent(event);
                    break;
                case ENTER_KEY_CODE:
                    setSaveNewComment(true);
                    break;
                default:
                    break;
            }

        }

        if (addNotesMode) {
            document.body.style.overflow = "hidden";
            window.addEventListener('keydown', handleKeyListener);
        } else {
            window.removeEventListener('keydown', handleKeyListener);
            document.body.style.overflow = "";
        }

        return () => {
            window.removeEventListener('keydown', handleKeyListener);
            document.body.style.overflow = "";
        }
    }, [addNotesMode]);

    useEffect(() => {
        if (saveNewComment) {
            commentText.trim().length && handleSave();
            setSaveNewComment(false);
        }
    }, [saveNewComment])

    return (<> {addNotesMode ?
        <div className={styles.overlay} onClick={handleCancel}>
            <p className={styles.escMessage} style={{ '--player-bottom': `${top + height + DEFAULT_MARGIN}px`, '--left': `${left + width / 2}px` }}>{t('video.player.add_note.cancel')}</p>
        </div> : null}
        {containerElementNode && ReactDOM.createPortal(
            <>
                {points.length ?
                    points?.map((comment) =>
                        <Fragment key={comment.id}>
                            <button className={cx(styles.commentPoint, { [styles.animate]: highlightCommentId === comment.id })}
                                style={{ top: `${comment.position.y}%`, left: `${comment.position.x}%`, zIndex: addNotesMode ? '50' : '51' }}
                                onClick={() => !addNotesMode && !screenfull.isFullscreen && handleCommentClick([comment])}
                                onMouseOver={handleCommentPointHover(comment.id)}
                                onMouseLeave={() => setHoverCommentId(null)}
                                aria-label='Note'
                            >
                                <Icon src='checkSelectOption' className={cx(styles.resolvedIcon, { [styles.show]: comment?.resolved })} />
                            </button>
                            {hoverCommentId === comment.id && <HoverPlayerContent comment={comment} commentStyles={{
                                zIndex: 999,
                                position: 'absolute', top: `${comment?.position?.y}%`,
                                left: `${comment?.position?.x}%`,
                                transform: `translate(${DEFAULT_MARGIN}px, ${DEFAULT_MARGIN}px)${comment?.position?.x > 50 ? ` translateX(calc(-100% - ${DEFAULT_MARGIN * 2}px))` : ''}${comment?.position?.y >= 70 ? ` translateY(calc(-100% - ${DEFAULT_MARGIN * 2}px))` : ''}`,
                                maxWidth: `${(((comment?.position?.x >= 50 ? comment?.position?.x : 100 - comment?.position?.x) / 100) * offsetWidth) - DEFAULT_MARGIN * 2}px`
                            }} maxHeight={containerElementNode?.offsetHeight * 0.6} />}
                        </Fragment>
                    ) : null}
                {addNotesMode && initPosition?.x && newCommentPosition !== null ? draggableChild : null}
                {addNotesMode && newCommentPosition !== null && !isMoving && commentWrapperChild}
            </>,
            containerElementNode 
        )}
    </>)
}

export default InPlayerCommentsOverlay;